import { KeyRound } from 'lucide-react';

import {
  BROKER_CONFIGS,
  getBrokerFields,
  isBrokerClientIdRequiredOnMap,
  shouldRenderBrokerClientId,
} from '../../config/brokerFields';

export function BrokerCredentialFields({
  brokerName,
  brokerClientId,
  totpKey,
  credentials,
  onTopLevelChange,
  onCredentialChange,
  disabled,
  mode,
  inputClassName = 'input-base',
}: {
  brokerName:         string;
  brokerClientId:     string;
  totpKey:            string;
  credentials:        Record<string, string>;
  onTopLevelChange:   (field: 'broker_client_id' | 'totp_key', value: string) => void;
  onCredentialChange: (key: string, value: string) => void;
  disabled:           boolean;
  mode:               'map' | 'update';
  inputClassName?:    string;
}) {
  const fields = getBrokerFields(brokerName);
  const isUpdate = mode === 'update';
  const accountIdentityLabel = BROKER_CONFIGS[brokerName]?.accountIdentityLabel ?? 'User ID';
  const showBrokerClientId = shouldRenderBrokerClientId(brokerName);
  const clientIdRequired = !isUpdate && isBrokerClientIdRequiredOnMap(brokerName);
  const clientIdRequirementLabel = clientIdRequired
    ? '(required)'
    : isUpdate
      ? '(leave blank to keep current)'
      : '(optional)';
  const totpKeyRequired = brokerName === 'kotakneo' && !isUpdate;

  return (
    <div className="space-y-4">
      {showBrokerClientId && (
        <div className="space-y-1">
          <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">
            {accountIdentityLabel} <span className="normal-case">{clientIdRequirementLabel}</span>
          </label>
          <input
            type="text"
            value={brokerClientId}
            onChange={(e) => onTopLevelChange('broker_client_id', e.target.value)}
            placeholder={isUpdate ? 'Leave blank to keep current' : `Your broker ${accountIdentityLabel.toLowerCase()}`}
            className={inputClassName}
            disabled={disabled}
            required={clientIdRequired}
          />
        </div>
      )}

      <div className="space-y-1">
        <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest flex items-center gap-1.5">
          <KeyRound size={11} /> TOTP Key{' '}
          <span className="normal-case font-sans">
            {totpKeyRequired ? '(required)' : isUpdate ? '(leave blank to keep current)' : '(optional)'}
          </span>
        </label>
        <input
          type="password"
          value={totpKey}
          onChange={(e) => onTopLevelChange('totp_key', e.target.value)}
          placeholder={isUpdate ? 'Leave blank to keep current' : 'TOTP seed key'}
          className={inputClassName}
          disabled={disabled}
          required={totpKeyRequired}
        />
      </div>

      {fields.map((field) => {
        const required = field.requiredOnMap === true && !isUpdate;
        const requirementLabel = isUpdate
          ? '(leave blank to keep current)'
          : field.requiredOnMap === true
            ? '(required)'
            : field.requiredOnMap === false
              ? '(optional)'
              : null;
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">
              {field.label}{' '}
              {requirementLabel && <span className="normal-case">{requirementLabel}</span>}
            </label>
            <input
              type={field.type}
              value={credentials[field.key] ?? ''}
              onChange={(e) => onCredentialChange(field.key, e.target.value)}
              placeholder={isUpdate ? 'Leave blank to keep current' : (field.placeholder ?? '')}
              className={inputClassName}
              disabled={disabled}
              required={required}
            />
          </div>
        );
      })}
    </div>
  );
}
